export type SegmentInput = {
  startSeconds: number | null
  endSeconds: number | null
  durationSeconds?: number | null
}

export type SegmentValidation =
  | { ok: true; startSeconds: number; endSeconds: number; durationPending: boolean }
  | { ok: false; error: string; durationPending?: boolean }
  | { ok: true; unconfigured: true }

/**
 * Configured segments require both values and 0 <= start < end.
 * When duration is available, require end <= duration.
 * Empty fields mean unconfigured, not zero-length.
 */
export function validateSegment(input: SegmentInput): SegmentValidation {
  const { startSeconds, endSeconds, durationSeconds } = input
  const startEmpty = startSeconds === null || startSeconds === undefined
  const endEmpty = endSeconds === null || endSeconds === undefined

  if (startEmpty && endEmpty) {
    return { ok: true, unconfigured: true }
  }

  if (startEmpty || endEmpty) {
    return { ok: false, error: 'Both Start and End are required to configure a segment.' }
  }

  if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) {
    return { ok: false, error: 'Start and End must be finite numbers.' }
  }

  if (startSeconds < 0 || endSeconds < 0) {
    return { ok: false, error: 'Timestamps cannot be negative.' }
  }

  if (!(startSeconds < endSeconds)) {
    return { ok: false, error: 'Start must be before End.' }
  }

  const durationKnown =
    durationSeconds !== null &&
    durationSeconds !== undefined &&
    Number.isFinite(durationSeconds) &&
    durationSeconds > 0

  if (durationKnown && endSeconds > durationSeconds!) {
    return {
      ok: false,
      error: `End must be within the video duration (${durationSeconds!.toFixed(1)}s).`,
    }
  }

  if (durationKnown && startSeconds > durationSeconds!) {
    return {
      ok: false,
      error: `Start must be within the video duration (${durationSeconds!.toFixed(1)}s).`,
    }
  }

  return {
    ok: true,
    startSeconds,
    endSeconds,
    durationPending: !durationKnown,
  }
}

/**
 * Clamp anchors while keeping a minimum gap of epsilon seconds.
 * Dragging one handle past the other pushes the opposite handle so a
 * stacked Start/End pair (e.g. at video end) can still be separated.
 */
export function clampAnchors(
  start: number,
  end: number,
  which: 'start' | 'end',
  next: number,
  duration?: number | null,
  epsilon = 0.1,
): { start: number; end: number } {
  let s = start
  let e = end
  const max = duration && duration > 0 ? duration : Number.POSITIVE_INFINITY

  if (which === 'start') {
    s = Math.max(0, Math.min(next, max))
    if (s > e - epsilon) {
      e = Math.min(max, s + epsilon)
      s = Math.max(0, Math.min(s, e - epsilon))
    }
  } else {
    e = Math.max(0, Math.min(next, max))
    if (e < s + epsilon) {
      s = Math.max(0, e - epsilon)
      e = Math.min(max, Math.max(e, s + epsilon))
    }
  }
  return { start: s, end: e }
}

export type SegmentSpan = {
  techniqueId: string
  startSeconds: number
  endSeconds: number
}

/**
 * Overlap produces a warning, not rejection.
 * Half-open style: touching endpoints (a.end === b.start) is not an overlap.
 */
export function findOverlappingSegments(
  candidate: SegmentSpan,
  others: SegmentSpan[],
): SegmentSpan[] {
  return others.filter((other) => {
    if (other.techniqueId === candidate.techniqueId) return false
    return (
      candidate.startSeconds < other.endSeconds && candidate.endSeconds > other.startSeconds
    )
  })
}

const DEFAULT_START = 0
const DEFAULT_LENGTH = 10
/** Minimum suggested window so Start/End handles are separable on the timeline. */
const MIN_EDITABLE_GAP = 1

/**
 * Suggested Start/End for a technique with no saved timing.
 * Start matches the previous technique's End (canonical order) so segments chain.
 * End defaults to the video duration when known so Start can be set freely first.
 * When the previous technique already ends at/near the video end, place a
 * default-length window at the end instead of a sub-second Start/End sliver
 * that stacks both handles and blocks dragging Start.
 */
export function suggestedUnsetSegment(input: {
  techniqueId: string
  orderedTechniqueIds: string[]
  segments: SegmentSpan[]
  durationSeconds?: number | null
}): { startSeconds: number; endSeconds: number } {
  const { techniqueId, orderedTechniqueIds, segments, durationSeconds } = input
  const index = orderedTechniqueIds.indexOf(techniqueId)
  const previousId = index > 0 ? orderedTechniqueIds[index - 1] : undefined
  const previous = previousId
    ? segments.find((s) => s.techniqueId === previousId)
    : undefined

  let start = previous ? previous.endSeconds : DEFAULT_START
  const duration =
    durationSeconds !== null &&
    durationSeconds !== undefined &&
    Number.isFinite(durationSeconds) &&
    durationSeconds > 0
      ? durationSeconds
      : null

  let end = duration !== null ? duration : start + DEFAULT_LENGTH

  if (duration !== null) {
    end = duration
    if (!(start < end - MIN_EDITABLE_GAP + 1e-9)) {
      start = Math.max(0, duration - DEFAULT_LENGTH)
    }
    if (!(start < end)) {
      start = Math.max(0, end - Math.min(DEFAULT_LENGTH, end))
    }
  }

  return { startSeconds: start, endSeconds: end }
}

